/**
 * 05/07 — Facing négocié : un fournisseur paie une tête de gondole (facings au
 * niveau des yeux) pour l'une de ses familles. Arbitrage pour l'apprenant :
 * coopération commerciale encaissée vs équilibre visibilité/marge du rayon.
 */
import { getFournisseursBySecteur, getSecteur } from './retail';
import { coefType } from './ameliorations';
import { computeFacing, suggererFacing, slotKey, nbColonnes, typeMagasinSecteur, merchSecteur, famillesActives } from './merchandising';

export const DUREE_CONTRAT_JOURS = 60;
export const VALIDITE_OFFRE_JOURS = 7;
export const RELATION_MIN_OFFRE = 40;
export const BONUS_RELATION_ACCEPT = 5;
const BASE_EUR_FACING_MOIS = 110;

export const etatFacingNegocie = (retail) => retail?.facingNegocie || { offres: [], contrats: [], cumulEur: 0 };

/** Emplacements réservés par les contrats actifs d'un secteur → { key: contratId }. */
export const verrousSecteur = (retail, secteurId) => {
  const v = {};
  etatFacingNegocie(retail).contrats.filter(c => c.secteurId === secteurId).forEach(c => c.slots.forEach(k => { v[k] = c.id; }));
  return v;
};

/** Force les facings réservés dans une grille (contrats actifs du secteur). */
export const forcerVerrous = (retail, secteurId, slots) => {
  const out = { ...(slots || {}) };
  etatFacingNegocie(retail).contrats.filter(c => c.secteurId === secteurId).forEach(c => c.slots.forEach(k => { out[k] = c.famId; }));
  return out;
};

const slotsTeteGondole = (retail, secteurId, facings) => {
  const cols = nbColonnes(typeMagasinSecteur(retail, secteurId));
  const pris = new Set(Object.keys(verrousSecteur(retail, secteurId)));
  const libres = [];
  for (let c = 0; c < cols && libres.length < facings; c++) {
    const k = slotKey('yeux', c);
    if (!pris.has(k)) libres.push(k);
  }
  return libres.length === facings ? libres : null;
};

/** Génère une offre pour un secteur (ou null). rng ∈ [0,1). */
export const genererOffreFacing = (retail, secteurId, { totalDays = 0, rng = Math.random } = {}) => {
  const fams = famillesActives(retail, secteurId);
  const fournisseurs = getFournisseursBySecteur(secteurId).filter(f => (retail.relationsFournisseurs?.[f.id] ?? 50) >= RELATION_MIN_OFFRE);
  if (!fams.length || !fournisseurs.length) return null;
  const f = fournisseurs[Math.floor(rng() * fournisseurs.length)];
  const candidates = fams.filter(x => f.familles.includes(x.id));
  if (!candidates.length) return null;
  const coefs = fams.map(x => x.coefficient).sort((a, b) => a - b);
  const median = coefs[Math.floor(coefs.length / 2)];
  const faibleMarge = candidates.filter(x => x.coefficient < median);
  const pool = faibleMarge.length && rng() < 0.7 ? faibleMarge : candidates;
  const fam = pool[Math.floor(rng() * pool.length)];
  const typeId = typeMagasinSecteur(retail, secteurId);
  const facings = nbColonnes(typeId) >= 8 ? 3 : 2;
  const slots = slotsTeteGondole(retail, secteurId, facings);
  if (!slots) return null;
  const montantMensuel = Math.round(facings * BASE_EUR_FACING_MOIS * coefType(typeId) * (1 + fam.part_demande) / 10) * 10;
  return {
    id: `off-${totalDays}-${secteurId}-${Math.floor(rng() * 1e6)}`,
    secteurId, fournisseurId: f.id, fournisseurNom: f.nom, famId: fam.id, famNom: fam.nom,
    facings, slots, montantMensuel, dureeJours: DUREE_CONTRAT_JOURS,
    proposeJour: totalDays, expireJour: totalDays + VALIDITE_OFFRE_JOURS,
  };
};

/** Arbitrage : score et effets du planogramme avant/après la tête de gondole. */
export const impactOffre = (retail, offre, month = null) => {
  const { secteurId } = offre;
  const base = forcerVerrous(retail, secteurId, merchSecteur(retail, secteurId).facing?.slots || suggererFacing(retail, { secteurId, month }));
  const apres = { ...base };
  offre.slots.forEach(k => { apres[k] = offre.famId; });
  const fxAvant = computeFacing(retail, base, { secteurId, month });
  const fxApres = computeFacing(retail, apres, { secteurId, month });
  return {
    scoreAvant: fxAvant.score,
    scoreApres: fxApres.score,
    panierAvant: fxAvant.panierPct,
    panierApres: fxApres.panierPct,
    multFamAvant: fxAvant.familleMult[offre.famId] ?? 1,
    multFamApres: fxApres.familleMult[offre.famId] ?? 1,
    gainContrat: Math.round(offre.montantMensuel * offre.dureeJours / 30),
    slotsApres: apres,
  };
};

export const nomSecteur = (id) => getSecteur(id)?.nom || id;
